import { ReactNode } from 'react'
import {
  theme,
  Box,
  Collapse,
  Tooltip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@black-bear-energy/rucksack'
import { NavLink } from 'react-router-dom'
import {
  NAV_TRANSITION,
  NAV_ITEM_BASE_SX,
  NAV_ITEM_ACTIVE_STYLES,
} from './constants'
import { useNavigation } from './NavigationContext'

/** Props for {@link SideNavigationItem}. Either an internal route (`to`) or external link (`href`). */
type SideNavigationItemProps =
  | InternalSideNavigationItemProps
  | ExternalSideNavigationItemProps

/** Shared props for all side navigation items. */
interface SideNavigationItemBaseProps {
  /** Display text shown next to the icon. */
  label: string
  /** Icon element rendered to the left of the label. */
  icon: ReactNode
}

/** Props for an internal route navigation item. */
interface InternalSideNavigationItemProps extends SideNavigationItemBaseProps {
  /** Internal route path passed to `NavLink`. */
  to: string
  /** When `true`, the item is only selected when the current route matches exactly (no prefix matching). */
  exact?: boolean
  /** Optional sub-items rendered in a collapsible section below this item. */
  children?: ReactNode
  /** Discriminant — must be `undefined` for internal items. */
  href?: undefined
}

/** Props for an external link navigation item. */
interface ExternalSideNavigationItemProps extends SideNavigationItemBaseProps {
  /** External URL opened in a new tab. */
  href: string
  /** Discriminant — must be `undefined` for external items. */
  to?: undefined
  /** Discriminant — must be `undefined` for external items. */
  exact?: undefined
  /** Discriminant — must be `undefined` for external items. */
  children?: undefined
}

/** A single item in the side navigation with an icon and label. */
export function SideNavigationItem({
  label,
  icon,
  to,
  href,
  exact = false,
  children,
}: SideNavigationItemProps) {
  const { expanded } = useNavigation()

  const listItemButtonSx = {
    ...NAV_ITEM_BASE_SX,
    minWidth: '45px',
    padding: 1,
  }

  const itemContent = (
    <>
      <ListItemIcon sx={{ paddingX: 0.25, minWidth: 0 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        slotProps={{ primary: { variant: 'body1' } }}
        sx={{
          marginLeft: 2,
          maxWidth: expanded ? 200 : 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transition: theme.transitions.create('max-width', NAV_TRANSITION),
        }}
      />
    </>
  )

  const link = to ? (
    <ListItemButton
      className="nav-link-unstyled"
      component={NavLink}
      to={to}
      exact={exact}
      activeClassName="active"
      sx={{
        ...listItemButtonSx,
        '&.active': NAV_ITEM_ACTIVE_STYLES,
      }}
    >
      {itemContent}
    </ListItemButton>
  ) : (
    <ListItemButton
      className="nav-link-unstyled"
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={listItemButtonSx}
    >
      {itemContent}
    </ListItemButton>
  )

  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Tooltip
        title={expanded ? '' : label}
        placement="right"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: { offset: [0, -10] },
              },
            ],
          },
        }}
      >
        {link}
      </Tooltip>
      {children && (
        <Collapse in={expanded} timeout={NAV_TRANSITION.duration}>
          <Box sx={{ paddingTop: 0.5 }}>{children}</Box>
        </Collapse>
      )}
    </Box>
  )
}
